function asNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function completedFixtures(fixtures = []) {
  return fixtures.filter((fixture) => fixture?.homeScore != null && fixture?.awayScore != null);
}

function clubFixtures(clubId, fixtures = []) {
  return completedFixtures(fixtures)
    .filter((fixture) => fixture.homeClubId === clubId || fixture.awayClubId === clubId)
    .sort((a, b) => Number(a.round || 0) - Number(b.round || 0));
}

function resultForClub(clubId, fixture) {
  const isHome = fixture.homeClubId === clubId;
  const scored = asNumber(isHome ? fixture.homeScore : fixture.awayScore);
  const conceded = asNumber(isHome ? fixture.awayScore : fixture.homeScore);
  return scored > conceded ? "W" : scored < conceded ? "L" : "D";
}

function longestRun(values, target) {
  let best = 0;
  let current = 0;
  values.forEach((value) => {
    current = value === target ? current + 1 : 0;
    best = Math.max(best, current);
  });
  return best;
}

export function buildClubPerformance(club, fixtures = []) {
  const matches = clubFixtures(club?.id, fixtures);
  const results = matches.map((fixture) => resultForClub(club.id, fixture));
  const goalsFor = matches.reduce((total, fixture) => {
    return total + asNumber(fixture.homeClubId === club.id ? fixture.homeScore : fixture.awayScore);
  }, 0);
  const goalsAgainst = matches.reduce((total, fixture) => {
    return total + asNumber(fixture.homeClubId === club.id ? fixture.awayScore : fixture.homeScore);
  }, 0);
  const homeMatches = matches.filter((fixture) => fixture.homeClubId === club.id);
  const awayMatches = matches.filter((fixture) => fixture.awayClubId === club.id);
  const operationalScores = matches.map((fixture) => asNumber(
    fixture.homeClubId === club.id ? fixture.homeOperationalScore : fixture.awayOperationalScore,
  )).filter((score) => score > 0);

  return {
    played: matches.length,
    wins: results.filter((result) => result === "W").length,
    draws: results.filter((result) => result === "D").length,
    losses: results.filter((result) => result === "L").length,
    goalsFor,
    goalsAgainst,
    goalDifference: goalsFor - goalsAgainst,
    homePlayed: homeMatches.length,
    awayPlayed: awayMatches.length,
    currentForm: results.slice(-5).reverse(),
    longestWinningStreak: longestRun(results, "W"),
    longestUnbeatenRun: (() => {
      let best = 0;
      let current = 0;
      results.forEach((result) => {
        current = result !== "L" ? current + 1 : 0;
        best = Math.max(best, current);
      });
      return best;
    })(),
    averageOperationalScore: operationalScores.length
      ? Math.round(operationalScores.reduce((sum, score) => sum + score, 0) / operationalScores.length)
      : 0,
    cleanSheets: matches.filter((fixture) => {
      const conceded = fixture.homeClubId === club.id ? fixture.awayScore : fixture.homeScore;
      return asNumber(conceded) === 0;
    }).length,
  };
}

export function buildAchievements(club, fixtures = [], standings = [], season = {}) {
  if (!club) return [];
  const stats = buildClubPerformance(club, fixtures);
  const position = standings.findIndex((entry) => entry.id === club.id) + 1;
  const achievements = [];

  if (position === 1 && standings.length > 1) {
    achievements.push({ id: "table-leader", icon: "♛", title: "Table Leader", detail: `Leading ${season.divisionName || "the division"}` });
  }
  if (stats.longestWinningStreak >= 3) {
    achievements.push({ id: "winning-streak", icon: "🔥", title: "Winning Streak", detail: `${stats.longestWinningStreak} consecutive wins` });
  }
  if (stats.longestUnbeatenRun >= 5) {
    achievements.push({ id: "unbeaten", icon: "🛡", title: "Unbeaten Run", detail: `${stats.longestUnbeatenRun} matches unbeaten` });
  }
  if (stats.averageOperationalScore >= 90) {
    achievements.push({ id: "operational-excellence", icon: "✦", title: "Operational Excellence", detail: `${stats.averageOperationalScore}% average operational score` });
  }
  if (stats.cleanSheets >= 3) {
    achievements.push({ id: "clean-sheet", icon: "◆", title: "Defensive Standard", detail: `${stats.cleanSheets} clean sheets` });
  }
  if (stats.goalsFor >= 10) {
    achievements.push({ id: "attack", icon: "⚡", title: "High Output", detail: `${stats.goalsFor} performance goals scored` });
  }
  if (stats.played > 0 && stats.losses === 0) {
    achievements.push({ id: "perfect-record", icon: "★", title: "Perfect Record", detail: "No defeats this season" });
  }

  return achievements;
}

export function buildTrophies(club, archives = []) {
  if (!club) return [];
  const trophies = [];
  archives.forEach((archive) => {
    const table = Array.isArray(archive.finalStandings) ? archive.finalStandings : [];
    const position = table.findIndex((entry) => entry.id === club.id) + 1;
    if (position === 1) {
      trophies.push({ id: `${archive.id}-champion`, icon: "🏆", title: "League Champion", season: archive.name || archive.id });
    }
    const promotionPlaces = Number(archive.promotionPlaces || 0);
    if (position > 0 && promotionPlaces > 0 && position <= promotionPlaces) {
      trophies.push({ id: `${archive.id}-promotion`, icon: "⬆", title: "Promotion", season: archive.name || archive.id });
    }
  });
  return trophies;
}

export function buildClubTimeline(club, fixtures = [], achievements = [], trophies = []) {
  if (!club) return [];
  const events = [];
  const firstFixture = clubFixtures(club.id, fixtures)[0];
  if (firstFixture) events.push({ id: "debut", label: "League debut", detail: `Round ${firstFixture.round}`, sort: Number(firstFixture.round || 0) });
  achievements.forEach((achievement, index) => events.push({ id: `achievement-${achievement.id}`, label: achievement.title, detail: achievement.detail, sort: 100 + index }));
  trophies.forEach((trophy, index) => events.push({ id: `trophy-${trophy.id}`, label: trophy.title, detail: trophy.season, sort: 200 + index }));
  return events.sort((a, b) => b.sort - a.sort);
}
